/**
 * useComments — Hook for ModComments CRUD operations.
 *
 * Wraps Refine's useList/useCreate with the "comments" resource
 * (mapped to coreBOS "ModComments" in mcpDataProvider.ts).
 */

import { useList, useCreate } from "@refinedev/core";
import { useCallback } from "react";

interface UseCommentsOptions {
  /** WSID of the parent entity to filter comments for */
  relatedTo?: string;
  /** Only fetch when true (default: true) */
  enabled?: boolean;
  /** Max comments to fetch (default: 50) */
  pageSize?: number;
  /** Polling interval in ms (default: 10000 for active threads) */
  refetchInterval?: number;
}

export function useComments({
  relatedTo,
  enabled = true,
  pageSize = 50,
  refetchInterval = 10000,
}: UseCommentsOptions = {}) {
  const filters = relatedTo
    ? [{ field: "related_to" as const, operator: "eq" as const, value: relatedTo }]
    : [];

  const { data, isLoading, refetch } = useList({
    resource: "comments",
    pagination: { pageSize },
    filters,
    sorters: [{ field: "createdtime", order: "desc" }],
    queryOptions: {
      enabled: enabled && (relatedTo ? !!relatedTo : true),
      refetchInterval,
    },
  });

  const comments = (data?.data || []) as any[];
  const total = data?.total || 0;

  return { comments, total, isLoading, refetch };
}

export function useCreateComment() {
  const { mutateAsync, isLoading } = useCreate();

  const createComment = useCallback(
    async ({
      content,
      relatedTo,
      assignedUserId = "19x1",
    }: {
      content: string;
      relatedTo: string;
      assignedUserId?: string;
    }) => {
      return mutateAsync({
        resource: "comments",
        values: {
          commentcontent: content,
          related_to: relatedTo,
          assigned_user_id: assignedUserId,
        },
      });
    },
    [mutateAsync]
  );

  return { createComment, isCreating: isLoading };
}
